/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { BsCheckCircle, BsExclamationCircle } from 'react-icons/bs';
import { getApiErrorMessage } from './apiError';

export type FeedbackState = { type: 'success' | 'danger'; message: string } | null;

export function useFeedback(successTimeoutMs = 3000) {
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const clear = useCallback(() => {
    window.clearTimeout(timer.current);
    setFeedback(null);
  }, []);

  const success = useCallback(
    (message: string) => {
      window.clearTimeout(timer.current);
      setFeedback({ type: 'success', message });
      timer.current = window.setTimeout(() => setFeedback(null), successTimeoutMs);
    },
    [successTimeoutMs],
  );

  const error = useCallback((err: unknown, fallback?: string) => {
    window.clearTimeout(timer.current);
    setFeedback({ type: 'danger', message: typeof err === 'string' ? err : getApiErrorMessage(err, fallback) });
  }, []);

  return { feedback, success, error, clear };
}

export const FeedbackAlert = ({ feedback, className = '' }: { feedback: FeedbackState; className?: string }) => {
  if (!feedback) return null;
  const Icon = feedback.type === 'success' ? BsCheckCircle : BsExclamationCircle;
  return (
    <Alert variant={feedback.type} className={`d-flex align-items-center gap-2 py-2 px-3 mb-2 ${className}`}>
      <Icon aria-hidden="true" />
      <span>{feedback.message}</span>
    </Alert>
  );
};
